// T-459 — Selettore scheda tecnica (PDF) condiviso tra editor prodotti e ricette.
// Componente CONTROLLATO dal parent (come la foto in FoodEditorModal): non tocca
// mai lo Storage da solo — accumula le pagine in `state.pending` e lascia al
// parent l'upload al salvataggio (offline-first). Le pagine possono essere foto
// scattate o file scelti (PDF/immagini) e vengono unite in un unico PDF.

import { useRef } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { documentTextOutline, cameraOutline, closeOutline, trashOutline, eyeOutline } from 'ionicons/icons';
import { getCaptureProvider } from '../lib/adapters/camera/index.js';
import { openTechSheet, type TechSheetState, type TechSheetPage } from '../lib/documents/techSheet.js';

interface TechSheetPickerProps {
  state: TechSheetState;
  onChange: (next: TechSheetState) => void;
  title?: string;
}

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '12px 14px',
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
};

const HEADING_STYLE: React.CSSProperties = {
  margin: '20px 4px 8px',
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

export function TechSheetPicker({ state, onChange, title }: TechSheetPickerProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Scatto foto → aggiunge una pagina immagine in coda.
  async function addPhoto(): Promise<void> {
    const shot = await getCaptureProvider().takePhoto('camera');
    if (!shot) return;
    const page: TechSheetPage = { b64: shot.base64, mime: shot.mime };
    onChange({ ...state, pending: [...state.pending, page], removed: false });
  }

  // File scelti (PDF/immagini) → una pagina per file, in coda.
  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const pages: TechSheetPage[] = [];
    for (const file of Array.from(files)) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('read_failed'));
        reader.readAsDataURL(file);
      });
      const comma = dataUrl.indexOf(',');
      const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      pages.push({ b64, mime: file.type || 'application/pdf' });
    }
    if (pages.length === 0) return;
    onChange({ ...state, pending: [...state.pending, ...pages], removed: false });
  }

  const hasPending = state.pending.length > 0;
  const hasSaved = !!state.path && !state.removed;

  return (
    <>
      <h2 style={HEADING_STYLE}>{title ?? '📄 Scheda tecnica'}</h2>
      <div style={CARD_STYLE}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => { void handleFiles(e.target.files); e.target.value = ''; }}
        />

        {hasPending ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
              {state.pending.length} {state.pending.length === 1 ? 'pagina pronta' : 'pagine pronte'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {state.pending.map((p, i) => (
                <span
                  key={i}
                  style={{
                    border: '1px solid #cbd5e1', borderRadius: 999, padding: '4px 10px',
                    fontSize: 12, color: '#475569', background: '#f8fafc',
                  }}
                >
                  {p.mime === 'application/pdf' ? '📄' : '🖼️'} Pag. {i + 1}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <IonButton size="small" fill="outline" onClick={() => { void addPhoto(); }} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={cameraOutline} slot="start" />
                Aggiungi pagina
              </IonButton>
              <IonButton size="small" fill="outline" onClick={() => fileInputRef.current?.click()} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={documentTextOutline} slot="start" />
                Aggiungi file
              </IonButton>
              <IonButton size="small" fill="clear" color="danger" onClick={() => onChange({ ...state, pending: [] })} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={trashOutline} slot="start" />
                Rimuovi tutte
              </IonButton>
            </div>
          </>
        ) : hasSaved ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <IonIcon icon={documentTextOutline} style={{ fontSize: 22, color: '#0066cc' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Scheda tecnica caricata</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <IonButton size="small" fill="outline" onClick={() => { if (state.path) void openTechSheet(state.path); }} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={eyeOutline} slot="start" />
                Visualizza
              </IonButton>
              <IonButton size="small" fill="outline" onClick={() => { void addPhoto(); }} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={cameraOutline} slot="start" />
                Sostituisci
              </IonButton>
              <IonButton size="small" fill="outline" onClick={() => fileInputRef.current?.click()} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={documentTextOutline} slot="start" />
                Da file
              </IonButton>
              <IonButton size="small" fill="clear" color="danger" onClick={() => onChange({ ...state, removed: true })} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                <IonIcon icon={closeOutline} slot="start" />
                Rimuovi
              </IonButton>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <IonButton size="small" fill="outline" onClick={() => { void addPhoto(); }} style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}>
              <IonIcon icon={cameraOutline} slot="start" />
              Scatta foto
            </IonButton>
            <IonButton size="small" fill="outline" onClick={() => fileInputRef.current?.click()} style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}>
              <IonIcon icon={documentTextOutline} slot="start" />
              Scegli file
            </IonButton>
          </div>
        )}
      </div>
    </>
  );
}
